import { useMemo } from 'react';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import csvToJson from 'convert-csv-to-json';

import { EmptyState } from '@/components/EmptyState';
import Loader from '@/components/Loader';
import { TABLE_ROW_HEIGHT_PX } from '@/constants/constants';
import { useAppSelector } from '@/hooks/redux-hooks';
import downloadTextFileFromDatalake from '@/utils/downloadTextFileFromDatalake';
import { parseAndFormatUserDateString } from '@/utils/parseUserDateString';

function parseCsvBetter(text: string) {
  return csvToJson
    .formatValueByType(true)
    .supportQuotedField(true)
    .fieldDelimiter(',')
    .csvStringToJson(text);
}
export type CSVTableMetadata = {
  align?: {
    [key: string]: 'left' | 'right' | 'center';
  };
};

const CsvTableWidget = ({
  fileName,
  groupName,
  metadata,
  layout
}: {
  fileName: string;
  groupName: string;
  layout?: { fullWidth?: boolean; height?: number };
  metadata?: CSVTableMetadata;
}) => {
  const { userConfig } = useAppSelector(state => state.userConfig);
  const {
    data: rows = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['csv-table', { fileName, groupName }],
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const text = await downloadTextFileFromDatalake(fileName, groupName);
      return parseCsvBetter(text);
    }
  });

  const header: string[] = useMemo(() => {
    if (rows.length === 0) return [];
    const first = rows[0];
    // If parser returns array of objects, use object keys as header
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      return Object.keys(first);
    }

    const firstRow = Array.isArray(rows[0]) ? (rows[0] as unknown[]) : [];
    return firstRow.map((v: unknown) => String(v));
  }, [rows]);

  const dataRows: unknown[] = useMemo(() => {
    if (rows.length === 0) return [];
    const first = rows[0];
    const isObjectRows = first && typeof first === 'object' && !Array.isArray(first);
    if (isObjectRows) return rows; // parser already used header row
    return rows.slice(1);
  }, [rows]);

  const theme = useTheme();

  if (isLoading) return <Loader />;

  if (error) {
    return <EmptyState />;
  }

  if (!rows.length) {
    return <EmptyState />;
  }

  return (
    <Paper>
      <TableContainer
        sx={{
          maxHeight: layout?.height
            ? `${layout?.height * TABLE_ROW_HEIGHT_PX + 47}px`
            : 'fit-content',
          pb: 2,
          borderCollapse: 'separate',
          borderSpacing: 0
        }}
      >
        <Table
          stickyHeader
          size='small'
          sx={{ tableLayout: 'auto' }}
        >
          {header.length > 0 && (
            <TableHead
              sx={{
                '& th': {
                  px: 1.5,
                  pb: 1,
                  borderBottom: `0.5px solid ${theme.palette.text.primary}`,
                  backgroundColor: 'background.paper'
                }
              }}
            >
              <TableRow>
                {header.map((h, i) => (
                  <TableCell
                    key={i}
                    align={metadata?.align?.[h] ?? 'left'}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {dataRows.map((r, ri) => (
              <TableRow
                key={ri}
                sx={{
                  '&:nth-child(1) td': {
                    pt: 1
                  },
                  '&:nth-of-type(even)': {
                    backgroundColor: '#121836'
                  },
                  '& td': {
                    px: 1.5
                  }
                }}
              >
                {(() => {
                  const formatMaybeDate = (value: unknown) => {
                    if (typeof value === 'string' && value.length <= 50) {
                      const { formatted, matched } = parseAndFormatUserDateString(
                        value,
                        userConfig
                      );
                      if (matched) return formatted;
                    }
                    return String(value ?? '');
                  };

                  if (r && typeof r === 'object' && !Array.isArray(r)) {
                    const obj = r as Record<string, unknown>;
                    return header.map((h, ci) => (
                      <TableCell
                        key={ci}
                        align={metadata?.align?.[h] ?? 'left'}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {formatMaybeDate(obj[h])}
                      </TableCell>
                    ));
                  }
                  if (Array.isArray(r)) {
                    return (r as unknown[]).map((val, ci) => {
                      const colName = header[ci];
                      return (
                        <TableCell
                          key={ci}
                          align={metadata?.align?.[colName] ?? 'left'}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {formatMaybeDate(val)}
                        </TableCell>
                      );
                    });
                  }
                  return null;
                })()}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default CsvTableWidget;
